import { isNumber } from 'lodash';
import { isNil } from 'ramda';
import { isHCSCScenario } from 'spw-components/worksheet/utils';
import { GROWTH_CALCULATORS } from 'spw-constants';
import {
  BUILDING_HEADCOUNT_SEATCOUNT,
  BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
  CalculatorType,
  FLOOR_HEADCOUNT_SEATCOUNT,
  Growth,
  ICategoryNote,
  Scenario,
  UsfType,
  WorkSeatsRoundingSetting,
} from 'spw-redux/modules/calculators/types';
import { IBuilding, IDepartment } from 'spw-redux/modules/projects/types';

export type PlainProgramScenario = Omit<
  ProgramScenario,
  | 'updateHeadcountWithVacancy'
  | 'updateConcurrentHeadcount'
  | 'updateWorkSeats'
  | 'applyWorkSeatsRoundingSetting'
  | 'toPlainObject'
  | 'toAPIScenario'
>;

export interface IProgramScenario {
  headcountWithUtilization: number;

  headcount: number;

  headcountWithVacancy: number;

  headcountUtilizationPercentage: number;

  workSeats: number;

  workSeatsOriginal: number;

  vacancyFactorPercentage: number;

  name: string;

  deskSharingRatio: number;

  note: string;

  id: number;

  index: number;

  subtotalMultiplierForCirculation: number;

  planningFactorPercentage: number;

  rsfLoadFactorPercentage: number;

  isRsfLoadFactorSetByUser: boolean;

  totalAvailableUSF?: number;

  totalAvailableRSF?: number;

  isDetailed: boolean;

  isShared: boolean;

  primaryLabel: string;

  secondaryLabel?: string;

  compoundLabel?: string;

  targetYear: number;

  startYear: number;

  calculatorType: CalculatorType;

  projectId: number;

  endYear: number;

  targetUSF?: number;

  departmentId: number;

  department?: IDepartment;

  growths: Growth[];

  headcountSeatCountGrowthRate?: number;

  headcountSeatCountBase?: number;

  growthYears?: number;

  storedWorkSeats: number;

  areStoredWorkSeatsOutdated: boolean;

  floorId?: number;

  base?: number;

  calculationsLabel: string;

  calculationsTitlePrefix: string;

  growthPercentage?: number;

  linkedScenarios?: PlainProgramScenario[];

  growth: Growth;

  building?: IBuilding;

  notes?: ICategoryNote[];

  usfType?: UsfType;

  toPlainObject: () => PlainProgramScenario;
}

export default class ProgramScenario implements IProgramScenario {
  headcountWithUtilization: number;

  headcount: number;

  headcountWithVacancy: number;

  headcountUtilizationPercentage: number;

  workSeats: number;

  workSeatsOriginal: number;

  vacancyFactorPercentage: number;

  name: string;

  deskSharingRatio: number;

  note: string;

  id: number;

  index: number;

  subtotalMultiplierForCirculation: number;

  planningFactorPercentage: number;

  rsfLoadFactorPercentage: number;

  isRsfLoadFactorSetByUser: boolean;

  totalAvailableUSF?: number;

  totalAvailableRSF?: number;

  isDetailed: boolean;

  isShared: boolean;

  primaryLabel: string;

  secondaryLabel?: string;

  workSeatsRoundingSetting?: WorkSeatsRoundingSetting;

  compoundLabel?: string;

  targetYear: number;

  startYear: number;

  calculatorType: CalculatorType;

  projectId: number;

  endYear: number;

  targetUSF?: number;

  departmentId: number;

  department?: IDepartment;

  growths: Growth[];

  headcountSeatCountGrowthRate?: number;

  headcountSeatCountBase?: number;

  growthYears?: number;

  storedWorkSeats: number;

  areStoredWorkSeatsOutdated: boolean;

  floorId?: number;

  base?: number;

  calculationsLabel = '';

  calculationsTitlePrefix = '';

  growthPercentage?: number;

  linkedScenarios?: PlainProgramScenario[];

  growth: Growth;

  building?: IBuilding;

  notes?: ICategoryNote[];

  usfType?: UsfType;

  worksheet?: number;

  constructor(headcount = 0, apiScenario: Scenario) {
    this.id = apiScenario.id;
    this.calculatorType = apiScenario.calculatorType;
    this.totalAvailableUSF = apiScenario.usf;
    this.totalAvailableRSF = apiScenario.usf * (1 + apiScenario.rsfLoadFactor);
    this.isDetailed = apiScenario.isDetailed;
    this.isShared = apiScenario.isShared;
    this.headcount = headcount;
    this.index = apiScenario.index;
    this.name = apiScenario.name;
    this.primaryLabel = apiScenario.name;
    this.building = apiScenario.building;
    this.workSeatsRoundingSetting = apiScenario.workSeatsRoundingSetting;
    this.planningFactorPercentage = apiScenario.subtotalProgramMultiplier;
    this.rsfLoadFactorPercentage = parseFloat(
      ((apiScenario.rsfLoadFactor || 0) * 100).toFixed(2)
    );
    this.isRsfLoadFactorSetByUser =
      apiScenario.rsfLoadFactor !== null &&
      apiScenario.rsfLoadFactor !== undefined;
    this.subtotalMultiplierForCirculation =
      apiScenario.subtotalBuildingMultiplier;
    this.headcountUtilizationPercentage = parseFloat(
      apiScenario.headcountUtilization as string
    );
    this.targetYear = apiScenario.targetYear;
    this.startYear = apiScenario.startYear;
    this.headcountSeatCountGrowthRate =
      apiScenario.headcountSeatCountGrowthRate;
    this.growthYears = apiScenario.growthYears;
    this.headcountSeatCountBase = apiScenario.headcountSeatCountBase;
    this.floorId = apiScenario.floorId;
    this.targetUSF = apiScenario.targetUsf;

    this.vacancyFactorPercentage = isNumber(apiScenario.vacancyFactor)
      ? apiScenario.vacancyFactor
      : parseFloat(apiScenario.vacancyFactor);

    this.updateHeadcountWithVacancy();
    this.updateConcurrentHeadcount();

    this.deskSharingRatio = isNumber(apiScenario.deskSharingRatio)
      ? apiScenario.deskSharingRatio
      : parseFloat(apiScenario.deskSharingRatio);

    this.updateWorkSeats();
    this.applyWorkSeatsRoundingSetting();

    this.endYear = apiScenario.startYear + apiScenario.growthYears;
    this.department = apiScenario.department;
    this.departmentId = apiScenario.department?.id || null;
    this.targetUSF = apiScenario.targetUsf;
    this.note = apiScenario.note;
    this.projectId = apiScenario.projectId;
    this.worksheet = apiScenario.worksheet?.id;

    this.storedWorkSeats = apiScenario.storedWorkSeats;
    this.areStoredWorkSeatsOutdated = apiScenario.areStoredWorkSeatsOutdated;
    this.floorId = apiScenario.floorId;
  }

  updateHeadcountWithVacancy = () => {
    const vacancyFactorMultiplier = 1 + this.vacancyFactorPercentage / 100;
    this.headcountWithVacancy = this.headcount * vacancyFactorMultiplier;
  };

  updateConcurrentHeadcount = () => {
    const vacancyFactorMultiplier = 1 + this.vacancyFactorPercentage / 100;
    const headcountUtilizationMultiplier =
      this.headcountUtilizationPercentage / 100;
    this.headcountWithUtilization =
      this.headcount * vacancyFactorMultiplier * headcountUtilizationMultiplier;
  };

  updateWorkSeats = () => {
    const workSeatsValue =
      this.headcountWithUtilization / this.deskSharingRatio;
    this.workSeats =
      Number.isFinite(workSeatsValue) && !Number.isNaN(workSeatsValue)
        ? workSeatsValue
        : 0;
    this.workSeatsOriginal = this.workSeats;
  };

  applyWorkSeatsRoundingSetting = () => {
    if (
      this.workSeats &&
      this.workSeatsRoundingSetting === WorkSeatsRoundingSetting.RoundUp
    )
      this.workSeats = Math.ceil(this.workSeats);
    if (
      this.workSeats &&
      this.workSeatsRoundingSetting === WorkSeatsRoundingSetting.RoundDown
    )
      this.workSeats = Math.floor(this.workSeats);
  };

  toPlainObject = (): PlainProgramScenario => JSON.parse(JSON.stringify(this));

  toAPIScenario = (): Scenario => ProgramScenario.toAPIScenario(this);

  static toAPIScenario = (
    s: ProgramScenario | PlainProgramScenario
  ): Scenario => {
    let growths = null;
    let headcountSeatCountGrowthRate = null;
    let headcountSeatCountBase = null;

    if (isHCSCScenario(s.calculatorType)) {
      headcountSeatCountGrowthRate = s.headcountSeatCountGrowthRate;
      headcountSeatCountBase = s.headcountSeatCountBase;
      if (isNil(s.headcountSeatCountGrowthRate)) {
        growths = s.growths?.[0].growthEntries.map((ge) => ge.value);
      }
    }

    return {
      rsfLoadFactor: s.isRsfLoadFactorSetByUser
        ? parseFloat((s.rsfLoadFactorPercentage / 100).toFixed(4))
        : null,
      calculatorType: s.calculatorType,
      usf: s.totalAvailableUSF,
      isDetailed: s.isDetailed,
      isShared: s.isShared,
      name: s.name,
      subtotalProgramMultiplier: s.planningFactorPercentage,
      subtotalBuildingMultiplier: s.subtotalMultiplierForCirculation,
      headcountUtilization: s.headcountUtilizationPercentage,
      targetYear: ProgramScenario.usesTargetYear(s) ? s.targetYear : null,
      workSeatsRoundingSetting: s.workSeatsRoundingSetting,
      startYear: s.startYear,
      endYear: s.endYear,
      vacancyFactor: s.vacancyFactorPercentage,
      deskSharingRatio: s.deskSharingRatio,
      targetUsf: s.targetUSF,
      department: s.department,
      departmentId: s.departmentId,
      projectId: s.projectId,
      id: s.id,
      index: s.index,
      note: s.note,
      storedWorkSeats: s.storedWorkSeats,
      areStoredWorkSeatsOutdated: s.areStoredWorkSeatsOutdated,
      floorId: s.floorId,
      growthYears: s.growthYears,
      base:
        s.calculatorType === GROWTH_CALCULATORS.macroProgramming
          ? s.headcount
          : undefined,
      building: s.building,
      headcountSeatCountGrowthRate,
      headcountSeatCountBase,
      growths,
    };
  };

  static usesTargetYear = (s: PlainProgramScenario) => {
    return [
      BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
      BUILDING_HEADCOUNT_SEATCOUNT,
      FLOOR_HEADCOUNT_SEATCOUNT,
    ].includes(s.calculatorType);
  };

  static isValid = (scenario: PlainProgramScenario) => {
    const {
      floorUsf,
      buildingUsf,
      buildingHeadcountSeatcountDepartments,
    } = GROWTH_CALCULATORS;

    switch (scenario?.calculatorType) {
      case floorUsf:
      case buildingUsf:
        return scenario.targetUSF > 0;
      case buildingHeadcountSeatcountDepartments:
        return Boolean(scenario.department);
      default:
        return true;
    }
  };
}
